import type { StudentSubmittedSurveysQuery } from 'core/graphql';

import type { ISurveyQuestion, SurveyQuestionKind } from './transformPendingSurveys';

/** One question paired with the student's own answer, ready to render. */
export interface ISubmittedAnswer {
  question: ISurveyQuestion;
  /** Human-readable text of what the student answered; '' when unanswered. */
  display: string;
}

export interface ISubmittedSurvey {
  responseId: string;
  title: string;
  groupName: string;
  lessonName: string;
  lessonNumber: number;
  submittedAt: string;
  answers: ISubmittedAnswer[];
}

const KNOWN_KINDS: SurveyQuestionKind[] = [
  'yes_no',
  'text',
  'rating',
  'single_choice',
  'multi_choice',
  'nps',
  'teacher_rating',
  'teacher_comment',
];

const isKnownQuestionKind = (value: string): value is SurveyQuestionKind =>
  (KNOWN_KINDS as string[]).includes(value);

/**
 * Decodes one stored answer into display text.
 *
 * `value` is a JSONString on the wire — the same encoding `buildSurveyAnswers`
 * writes, so decoding belongs here and components never touch it. A value that
 * fails to parse (hand-edited row, a type this build does not know) degrades to
 * the raw string rather than throwing: a survey the student demonstrably filled
 * must never render as a crash.
 */
const displayAnswer = (question: ISurveyQuestion, raw: string): string => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    // A yes_no answer must STILL come back as a token, even from an unparsable
    // value (a legacy row storing bare `true`): the component feeds it to `t()`,
    // and returning the raw string would print «surveys.submitted.true».
    if (question.questionType === 'yes_no') {
      return raw.trim().toLowerCase() === 'true' ? 'yes' : 'no';
    }
    return raw;
  }
  if (parsed === null || parsed === undefined) return '';

  switch (question.questionType) {
    case 'yes_no':
      // Localisation is the caller's job; the two labels are the only strings
      // this transform cannot resolve on its own, so they travel as TOKENS —
      // and only ever these two. The component feeds the token to `t()`, so a
      // stray value here would render as the literal key
      // («surveys.submitted.true») on screen.
      return parsed === true || parsed === 'true' ? 'yes' : 'no';
    case 'single_choice': {
      const index = Number(parsed);
      return question.options[index] ?? String(parsed);
    }
    case 'multi_choice': {
      const indexes = Array.isArray(parsed) ? parsed : [];
      return indexes.map((i) => question.options[Number(i)] ?? String(i)).join(', ');
    }
    case 'rating':
    case 'teacher_rating':
    case 'nps':
      return String(parsed);
    default:
      return String(parsed);
  }
};

/**
 * Normalizes `mySubmittedSurveys` into view models, joining each answer onto its
 * question so the UI never has to index by id.
 *
 * Questions with no answer row are kept with an empty `display`: an OPTIONAL
 * question the student skipped is information, and dropping it would make the
 * archive look like a different survey than the one they filled.
 */
export const transformSubmittedSurveys = (
  data: StudentSubmittedSurveysQuery | undefined,
): ISubmittedSurvey[] =>
  (data?.mySubmittedSurveys ?? []).map((survey) => {
    const byQuestion = new Map<string, string>();
    survey.answers.forEach((answer) => {
      byQuestion.set(String(answer.questionId), answer.value);
    });

    return {
      responseId: survey.responseId,
      title: survey.surveyTitle,
      groupName: survey.groupName,
      lessonName: survey.lessonName,
      lessonNumber: survey.lessonNumber,
      submittedAt: survey.submittedAt,
      answers: survey.questions.map((raw) => {
        const question: ISurveyQuestion = {
          id: raw.id,
          number: raw.number,
          text: raw.text,
          questionType: isKnownQuestionKind(raw.questionType) ? raw.questionType : 'text',
          required: raw.required,
          options: raw.options ?? [],
        };
        const stored = byQuestion.get(String(question.id));
        return {
          question,
          display: stored === undefined ? '' : displayAnswer(question, stored),
        };
      }),
    };
  });
